import React, { ReactNode } from 'react'
import { useRouteMatch } from 'react-router'
import { InView } from 'react-intersection-observer'

import {
  useFlag,
  isProjectTrashRoute,
  isDraftsRoute,
  isTrashRoute,
  useFolderTerm,
} from '@sketch/modules-common'

import {
  Button,
  Dropdown,
  Tooltip,
  useModalContext,
  useResponsiveDropdown,
} from '@sketch/components'

import {
  CopyProjectAction,
  DeleteProjectAction,
  LeaveProjectAction,
  EditProjectAction,
  RenameProjectAction,
  PinProjectAction,
  MoveProjectAction,
} from '../ProjectDropdownActions'

import ProjectMembershipsTooltip from '../ProjectMembershipsTooltip'

import SharingButton from './SharingButton'

import {
  Dots,
  ButtonWrapper,
  CollectionIcon,
  CreateNestedProjectButton,
  Divider,
  CreateProjectIcon,
} from './ProjectHeader.styles'

import { useGetProjectMembershipsQuery } from '@sketch/gql-types'
import { ProjectHeaderActionsProps } from './types'
import { CreateCollectionModal } from '../../../collections/modals'

import ArchiveProjectModal from '../../modals/ArchiveProjectModal'
import UnarchiveProjectModal from '../../modals/UnarchiveProjectModal'
import { CreateNestedProjectModal } from '../../../projects/modals'
import { isMyDrafts } from '../../utils'
import { userIsEditorOrAdmin } from '../../../workspace/utils'
import { getShowArchiveAction } from '../../../storageItems/utils'
import { MoveStorageItemModal } from '../../../storageItems'
import { ProjectTrashedDropdown } from '../ProjectDropdown'

const ActionDropdownWrapper = ({ children }: { children: ReactNode }) => {
  const folderTerm = useFolderTerm()
  return (
    <div onClick={event => event.stopPropagation()}>
      <Dropdown.Header>{folderTerm.upper}</Dropdown.Header>
      {children}
    </div>
  )
}

export const ProjectHeaderActions: React.FCC<ProjectHeaderActionsProps> = ({
  project,
  workspaceAccessLevel,
  workspace,
  onRenameProject,
  headerSlim,
}) => {
  const { identifier: workspaceId } = workspace

  const isNestedProjectsOn = useFlag('nested-projects')

  const { path } = useRouteMatch()
  const isEditorOrAdmin = userIsEditorOrAdmin(workspace)
  const userCanRenameAndDeleteProject =
    isDraftsRoute(path) || isTrashRoute(path) ? false : isEditorOrAdmin

  const isTrashedProjectView = isProjectTrashRoute(path)

  const { showModal } = useModalContext()

  const { error, loading, data } = useGetProjectMembershipsQuery({
    variables: {
      projectIdentifier:
        project.topLevelProject?.identifier || project.identifier,
    },
  })

  const handleCreateNestedProject = () => {
    if (isNestedProjectsOn) {
      showModal(CreateNestedProjectModal, {
        project,
        workspaceIdentifier: workspaceId,
      })
    } else {
      showModal(CreateCollectionModal, {
        project,
        workspaceIdentifier: workspaceId,
      })
    }
  }

  let dropdownAction = null

  const showLeaveAction = project.userCanLeave

  // Only show the divider if there will be buttons beneath it
  const showDivider = userCanRenameAndDeleteProject || showLeaveAction
  const showArchive = getShowArchiveAction(workspace, project)

  const pinned = project.pinnedByCurrentUserAt
  const isArchived = !!project.archivedAt

  if (project.deletedAt) {
    dropdownAction = (
      <ProjectTrashedDropdown project={project} workspace={workspace} />
    )
  } else {
    dropdownAction = (
      <>
        <CopyProjectAction project={project} workspaceId={workspaceId} />
        {showDivider && <Dropdown.Divider />}
        {userCanRenameAndDeleteProject && isNestedProjectsOn && (
          <EditProjectAction workspaceId={workspaceId} project={project} />
        )}
        {userCanRenameAndDeleteProject && (
          <RenameProjectAction onRename={() => onRenameProject(true)} />
        )}
        {!isArchived && project.projectType === 'STANDARD' && (
          <PinProjectAction
            projectIdentifier={project.identifier}
            pinned={pinned}
          />
        )}
        {userCanRenameAndDeleteProject && isNestedProjectsOn && !isArchived && (
          <MoveProjectAction
            workspaceIdentifier={workspaceId}
            project={project}
          />
        )}
        {showDivider && <Dropdown.Divider />}
        {showArchive &&
          (project.archivedAt ? (
            isNestedProjectsOn ? (
              <Dropdown.Item
                onClick={() =>
                  showModal(MoveStorageItemModal, {
                    item: project,
                    workspaceIdentifier: workspaceId,
                    parentProjectIdentifier: project.parentProjectIdentifier,
                    originType: 'stashed',
                    action: 'unarchive',
                  })
                }
              >
                Unarchive&hellip;
              </Dropdown.Item>
            ) : (
              <Dropdown.Item
                onClick={() => {
                  showModal(UnarchiveProjectModal, {
                    projectIdentifier: project.identifier,
                    projectName: project.name,
                    workspaceIdentifier: workspaceId,
                  })
                }}
              >
                Unarchive&hellip;
              </Dropdown.Item>
            )
          ) : (
            <Dropdown.Item
              onClick={() => {
                showModal(ArchiveProjectModal, {
                  project,
                  workspaceIdentifier: workspaceId,
                })
              }}
            >
              Archive&hellip;
            </Dropdown.Item>
          ))}
        {showLeaveAction && (
          <LeaveProjectAction
            workspaceIdentifier={workspaceId}
            projectIdentifier={project.identifier}
            projectName={project.name}
          />
        )}
        {userCanRenameAndDeleteProject && (
          <DeleteProjectAction project={project} workspaceId={workspaceId} />
        )}
      </>
    )
  }

  const [content, buttonProps, { setVisible }] = useResponsiveDropdown({
    dropdown: ActionDropdownWrapper,
    dropdownProps: { children: dropdownAction },
    usePortal: true,
    placement: 'bottom-start',
  })

  const handleOtherActionsDropdown = (visible: boolean) => {
    if (!visible) {
      setVisible(false)
    }
  }

  const createNestedProjectButton = (
    <Tooltip
      placement="top"
      content={isNestedProjectsOn ? 'Create a folder…' : 'Create Collection'}
    >
      <CreateNestedProjectButton
        onClick={handleCreateNestedProject}
        $isNestedProjectsOn={isNestedProjectsOn}
      >
        {isNestedProjectsOn ? <CreateProjectIcon /> : <CollectionIcon />}
      </CreateNestedProjectButton>
    </Tooltip>
  )

  if (!isNestedProjectsOn && project.projectType === 'PERSONAL_DRAFTS') {
    if (!isEditorOrAdmin) {
      return null
    }

    return <ButtonWrapper>{createNestedProjectButton}</ButtonWrapper>
  }

  return (
    <ButtonWrapper $headerSlim={headerSlim}>
      {!project.deletedAt && (
        <>
          {project.projectType === 'STANDARD' && (
            <ProjectMembershipsTooltip
              workspace={workspace}
              memberships={data?.project.members}
              shouldShowWorkspace={workspaceAccessLevel !== 'NONE'}
              workspaceAccessLevel={workspaceAccessLevel}
              disabled={loading || !!error}
            >
              <SharingButton
                project={project}
                workspace={workspace}
                userCanRenameAndDeleteProject={userCanRenameAndDeleteProject}
                memberships={data?.project.members}
                headerSlim={headerSlim}
              />
            </ProjectMembershipsTooltip>
          )}

          {isEditorOrAdmin &&
            !project.archivedAt &&
            !isTrashedProjectView &&
            createNestedProjectButton}
        </>
      )}
      {project.archivedAt && (
        <Button
          variant="secondary-untinted"
          onClick={() =>
            showModal(MoveStorageItemModal, {
              item: project,
              workspaceIdentifier: workspaceId,
              parentProjectIdentifier: project.parentProjectIdentifier,
              originType: 'stashed',
              action: 'unarchive',
            })
          }
        >
          Unarchive…
        </Button>
      )}
      {!isMyDrafts(project) && (
        <>
          <Button
            data-testid="project-actions"
            variant="secondary-untinted"
            {...buttonProps}
          >
            <InView onChange={handleOtherActionsDropdown}>
              <span className="sr-only">Other Actions</span>
              <Dots />
            </InView>
          </Button>
          {content}
        </>
      )}
      {headerSlim && <Divider />}
    </ButtonWrapper>
  )
}
